import { IS_DEV_MODE } from '@/const/static';
import { useLocalStorage } from '@/utils/useStorage';
import { isObject, isString } from '@/utils/util';
import axios from 'axios';

import Vue from 'vue';
import VueI18n from 'vue-i18n';
import { alert, showLoading } from '../dialog/api';

import jinLee from './jinlee-en';

Vue.use(VueI18n);

export const langStorage = useLocalStorage('LANGUAGE');

export const DEFAULT_LANGUAGE = 'ko';

const supportsLangs = [DEFAULT_LANGUAGE, 'en'];

const userLang = window.navigator?.language?.split('-')[0];

export const i18n = new VueI18n({
  locale: langStorage.get(
    supportsLangs.includes(userLang) ? userLang : DEFAULT_LANGUAGE,
  ),
  fallbackLocale: DEFAULT_LANGUAGE,
});

export const canTranslate = text =>
  text && i18n._getMessages?.()[i18n.locale]?.[text] != null;

export const $t = (value, ...args) =>
  i18n.locale == DEFAULT_LANGUAGE || !canTranslate(value)
    ? value
    : i18n.t(value, ...args);
export const isLocaleDefault = () => i18n.locale == DEFAULT_LANGUAGE;

/**
 *
 * @param {string} text
 * @returns {boolean}
 */

export const loadLocale = (loadedLangs => async (lang = i18n.locale) => {
  if (loadedLangs.includes(lang)) return;
  IS_DEV_MODE && console.log(`fetch ${lang}.json`);
  const close = showLoading();

  await axios(`/locale/${lang}.json`)
    .then(({ data }) => {
      loadedLangs.push(lang);
      i18n.mergeLocaleMessage(lang, data);

      i18n.mergeLocaleMessage('en', jinLee);
    })
    .catch(async () => {
      await alert('지원되지 않는 언어 입니다.');
      i18n.locale = DEFAULT_LANGUAGE;
    })
    .finally(close);
})([DEFAULT_LANGUAGE]);

export const ibSheetHeaderTranslator = context => {
  // sheet 에 Solid 버튼  도 translate 해야함

  if (!context?.options || i18n.locale == DEFAULT_LANGUAGE) return;

  context.options.Cfg = { ...(context.options.Cfg || {}), MsgLocale: 'En' };
  try {
    const translateTarget = [];

    if (Array.isArray(context.options.Solid))
      context.options.Solid.forEach(item => {
        const buttons = isString(item.Cells) ? item.Cells?.split(',') : [];
        buttons.forEach(key => {
          if (item[key]?.Value) item[key].Value = $t(item[key].Value);
        });
      });
    [
      ...(context.options.Cols || []),
      ...(context.options.LeftCols || []),
      ...(context.options.RightCols || []),
    ].forEach(col => {
      if (!isObject(col)) return;
      col.Header = $t(col.Header);
      if (col.Type == 'Enum') {
        col.Enum =
          col.Enum?.split('|')
            .map(v => $t(v))
            .join('|') || '';

        col.EnumStrictMode = 1;
      }
      if (col.Type == 'Button' && col.ButtonText) {
        col.ButtonText = $t(col.ButtonText);
      }
      if (col.Type == 'Button' || col.Type == 'Enum')
        translateTarget.push(col.Name);
    });
    context.options.Events ??= {};
    const userEvent = context.options.Events.onBeforeDataLoad;
    context.options.Events.onBeforeDataLoad = eventParam => {
      userEvent?.(eventParam);
      eventParam?.data?.forEach(row => {
        translateTarget.forEach(key => {
          row[key] = $t(row[key]);
        });
      });
    };
  } catch (error) {
    console.error(error);
  }
};

const i18nVuetifyComponent = require.context(
  '@/plugins/locales/components',
  false,
  /I[A-Z]\w+\.(vue)$/,
);

/**
 * @param {import('vue').VueConstructor<Vue>} app
 */
export const i18nComponentInstall = app => {
  app.prototype.$t = $t;
  app.prototype.$isLocaleDefault = isLocaleDefault;
  app.directive('t', {
    bind(el, { value }) {
      el.textContent = $t(value);
    },
    update(el, { value }) {
      el.textContent = $t(value);
    },
  });
  i18nVuetifyComponent.keys().forEach(fileName => {
    const componentName = fileName
      .split('/')
      .pop()
      .replace(/\.\w+$/, '');
    Vue.component(componentName, i18nVuetifyComponent(fileName).default);
  });
};
